import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Send, Sparkles, Loader2, ChevronDown } from "lucide-react";
import OSLayout from "../../components/os/OSLayout";
import { API, api } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";

const MODELS = [
  { key: "claude", label: "Claude Sonnet 4.6", vendor: "Anthropic" },
  { key: "gpt", label: "ChatGPT · GPT-5.4", vendor: "OpenAI" },
  { key: "gemini", label: "Gemini 3.1 Pro", vendor: "Google" },
];

export default function Assistant() {
  const { t } = useI18n();
  const SUGGESTIONS = t("os.assistant.suggestions");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [model, setModel] = useState(() => localStorage.getItem("omnia_ai_model") || "claude");
  const scrollRef = useRef(null);
  const sessionId = "main";

  const changeModel = (m) => { setModel(m); localStorage.setItem("omnia_ai_model", m); };

  useEffect(() => {
    api.get(`/ai/history/${sessionId}`).then((r) => {
      if (r.data?.length) setMessages(r.data.map((m) => ({ role: m.role, content: m.content })));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const token = localStorage.getItem("omnia_token");
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: msg, session_id: sessionId, model }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line) continue;
          try {
            const evt = JSON.parse(line);
            if (evt.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + evt.delta };
                return copy;
              });
            }
          } catch (_) {}
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "OMNIA AI: error de conexión. Inténtalo de nuevo." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <OSLayout title={t("os.titles.assistant")}>
      <div className="max-w-3xl mx-auto flex flex-col h-[calc(100vh-9.5rem)]">
        {messages.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
            <div className="w-14 h-14 rounded-full bg-gradient-violet flex items-center justify-center mb-6">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <h2 className="font-editorial text-4xl tracking-tight">{t("os.assistant.title")}</h2>
            <p className="mt-3 text-navy/50 max-w-md">{t("os.assistant.subtitle")}</p>
            <div className="mt-8 grid sm:grid-cols-2 gap-3 w-full max-w-xl">
              {SUGGESTIONS.map((s) => (
                <button key={s} data-testid="ai-suggestion" onClick={() => send(s)}
                  className="text-left text-sm bg-white border border-navy/10 rounded-lg px-4 py-3 hover:border-violet transition-colors">
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div ref={scrollRef} data-testid="ai-messages" className="flex-1 overflow-y-auto thin-scroll space-y-6 pb-4">
            {messages.map((m, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-5 py-3.5 text-[0.95rem] leading-relaxed whitespace-pre-wrap ${
                  m.role === "user" ? "bg-navy text-cream" : "bg-white border border-navy/10 text-navy"
                }`}>
                  {m.content || (streaming && i === messages.length - 1 ? <Loader2 className="w-4 h-4 animate-spin text-violet" /> : "")}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-4">
          <div className="flex items-center justify-between mb-2 px-1">
            <div className="relative inline-flex items-center">
              <label className="sr-only" htmlFor="ai-model">Modelo</label>
              <select id="ai-model" data-testid="ai-model-select" value={model}
                onChange={(e) => changeModel(e.target.value)}
                className="appearance-none bg-white border border-navy/10 rounded-full pl-4 pr-9 py-2 text-xs text-navy/70 outline-none focus:border-violet transition-colors cursor-pointer hover:border-navy/25">
                {MODELS.map((m) => <option key={m.key} value={m.key}>{`${m.label} · ${m.vendor}`}</option>)}
              </select>
              <ChevronDown className="w-3.5 h-3.5 text-navy/40 absolute right-3 pointer-events-none" />
            </div>
            <span className="text-[11px] text-navy/35">{t("os.assistant.poweredBy") || "Powered by OMNIA Intelligence"}</span>
          </div>
          <div className="flex items-center gap-2 bg-white border border-navy/10 rounded-full pl-5 pr-2 py-2 focus-within:border-violet transition-colors">
            <input
              data-testid="ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("os.assistant.placeholder")}
              className="flex-1 bg-transparent outline-none text-navy py-2"
            />
            <button data-testid="ai-send" disabled={streaming || !input.trim()}
              className="w-10 h-10 rounded-full bg-gradient-violet text-white flex items-center justify-center disabled:opacity-40 transition-opacity">
              {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </form>
      </div>
    </OSLayout>
  );
}
